// validation.js is the frontend mirror of the backend's typed validation
// engine and deterministic pipeline for the Terreno domain
// (visor-mapas-backend/services/terrain/{engine,conditions,pipeline,regex_safe}.go).
//
// Purpose and authority
// ----------------------
// This module exists purely for UX feedback (instant field errors, live
// visibility) while a Respondente/Administrador_Terreno fills a form. It
// is NEVER authoritative: the backend (`RunValidationPipeline` in Go)
// re-validates every submission from scratch and is the only source of
// truth for acceptance, persisted values and security decisions. Nothing
// in this file should be used to decide whether to trust, store or
// publish a value; it only decides what to show the user before they
// submit.
//
// Contract mirrored here
// -----------------------
// The same "questionType -> value shape -> operators -> validation rules"
// table as engine.go, the same Condición evaluation semantics as
// pipeline.go's evaluateCondition, and the same deterministic pipeline
// shape (canonicalize -> reject unknown/duplicate/system-controlled ->
// type-check -> visibility -> strip hidden values -> apply validations on
// visible questions -> sorted field errors). Running the shared contract
// corpus (src/services/terrain/testdata/corpus.v1.json, byte-identical to
// the backend copy) through `runValidationPipeline` must produce the same
// `accepted`, `normalizedAnswers`, `visibleQuestionIds` and `fieldErrors`
// codes as the Go pipeline for every case (Requirements 3.1, 3.4, 3.5).
//
// Known discrepancy: regular expressions and ReDoS
// -------------------------------------------------
// The backend compiles "pattern" validation rules with Go's `regexp`
// package, which is backed by RE2 and therefore guarantees linear-time
// matching with no catastrophic backtracking, regardless of the pattern
// (see regex_safe.go for the full rationale and its additional
// compiled-program-size limit). JavaScript's native `RegExp` has no RE2
// equivalent: it is a backtracking engine, so a pathological pattern
// (nested unbounded quantifiers, etc.) *can* exhibit exponential-time
// ReDoS behavior in this validator that would never occur server-side.
// This module intentionally does not attempt to reimplement RE2 in
// JavaScript. Mitigations applied here are limited to reusing the same
// `MAX_PATTERN_LENGTH` bound as the backend and evaluating patterns only
// against user-entered field values (bounded input length), never against
// untrusted, attacker-controlled strings of arbitrary size. Because this
// validator is UX-only, a slow/hanging match here degrades the UI for the
// current user; it cannot corrupt data, since the backend enforces the
// same rule safely with RE2 on submit.
//
// Requirements: 3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 3.7, 3.8 (spec: levantamiento-terreno)

/** @typedef {"text"|"number"|"date"|"boolean"|"optionSingle"|"optionMulti"|"location"|"structured"} ValueShape */

/**
 * QUESTION_TYPE_SPECS is the single source of truth for
 * "questionType -> value shape -> operators -> validation rules" on the
 * frontend, mirroring questionTypeSpecs in engine.go. No other function in
 * this module should hardcode a type's shape, operators or validation
 * rules; they must all go through this table.
 */
export const QUESTION_TYPE_SPECS = Object.freeze({
  shortText: {
    valueShape: "text",
    operators: ["equals", "notEquals", "in", "notIn", "isEmpty", "isNotEmpty"],
    validationRules: ["minLength", "maxLength", "pattern"],
  },
  longText: {
    valueShape: "text",
    operators: ["equals", "notEquals", "in", "notIn", "isEmpty", "isNotEmpty"],
    validationRules: ["minLength", "maxLength", "pattern"],
  },
  number: {
    valueShape: "number",
    operators: [
      "equals", "notEquals", "in", "notIn",
      "greaterThan", "greaterOrEqual", "lessThan", "lessOrEqual",
      "isEmpty", "isNotEmpty",
    ],
    validationRules: ["numberRange"],
  },
  date: {
    valueShape: "date",
    operators: [
      "equals", "notEquals",
      "greaterThan", "greaterOrEqual", "lessThan", "lessOrEqual",
      "isEmpty", "isNotEmpty",
    ],
    validationRules: ["dateRange"],
  },
  singleSelect: {
    valueShape: "optionSingle",
    operators: ["equals", "notEquals", "in", "notIn", "isEmpty", "isNotEmpty"],
    validationRules: [],
  },
  multiSelect: {
    valueShape: "optionMulti",
    operators: ["containsAny", "containsAll", "in", "notIn", "isEmpty", "isNotEmpty"],
    validationRules: ["itemCount"],
  },
  check: {
    valueShape: "boolean",
    operators: ["isChecked", "isNotChecked"],
    validationRules: [],
  },
  table: {
    valueShape: "structured",
    operators: [],
    validationRules: ["tableCellRules", "itemCount"],
  },
  repeater: {
    valueShape: "structured",
    operators: [],
    validationRules: ["itemCount"],
  },
  file: {
    valueShape: "structured",
    operators: [],
    validationRules: ["attachmentLimit"],
  },
  photo: {
    valueShape: "structured",
    operators: [],
    validationRules: ["attachmentLimit"],
  },
  location: {
    valueShape: "location",
    operators: [],
    validationRules: [],
  },
});

/** Same bound as MaxPatternLength in regex_safe.go (see file header). */
export const MAX_PATTERN_LENGTH = 200;

/**
 * Field error codes produced by this validator. These match the public
 * contract fixed by the shared corpus (testdata/corpus.v1.json), not the
 * internal Go constant names in pipeline.go (e.g. ANSWER_MIN_LENGTH).
 */
export const FieldErrorCode = Object.freeze({
  DUPLICATE_QUESTION_ID: "DUPLICATE_QUESTION_ID",
  UNKNOWN_QUESTION_ID: "UNKNOWN_QUESTION_ID",
  SYSTEM_CONTROLLED_FIELD: "SYSTEM_CONTROLLED_FIELD",
  TYPE_MISMATCH: "TYPE_MISMATCH",
  UNKNOWN_OPTION_ID: "UNKNOWN_OPTION_ID",
  REQUIRED: "REQUIRED",
  MIN_LENGTH: "MIN_LENGTH",
  MAX_LENGTH: "MAX_LENGTH",
  PATTERN: "PATTERN",
  NUMBER_RANGE: "NUMBER_RANGE",
  DATE_RANGE: "DATE_RANGE",
  ITEM_COUNT: "ITEM_COUNT",
});

function specForType(qType) {
  return QUESTION_TYPE_SPECS[qType] || null;
}

/** Mirrors IsOperatorAllowed in engine.go. */
export function isOperatorAllowed(qType, operator) {
  const spec = specForType(qType);
  if (!spec) return false;
  return spec.operators.indexOf(operator) !== -1;
}

/** Mirrors IsReferenceableByCondition in engine.go. */
export function isReferenceableByCondition(qType) {
  const spec = specForType(qType);
  if (!spec) return false;
  return spec.valueShape !== "structured";
}

function isPlainObject(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Mirrors isEmptyAnswerValue in pipeline.go: nil/empty string/empty array
 * count as "no answer"; booleans and numeric zero are legitimate answers.
 */
function isEmptyAnswerValue(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value === "";
  if (Array.isArray(value)) return value.length === 0;
  if (isPlainObject(value)) return Object.keys(value).length === 0;
  return false;
}

function isNumericValue(value) {
  return typeof value === "number" && Number.isFinite(value);
}

/** Mirrors parseFlexibleDate in pipeline.go (RFC3339 or YYYY-MM-DD). */
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

function parseFlexibleDate(value) {
  if (typeof value !== "string" || value === "") return null;
  if (DATE_ONLY_RE.test(value)) {
    const t = Date.parse(value + "T00:00:00Z");
    return Number.isNaN(t) ? null : t;
  }
  const t = Date.parse(value);
  return Number.isNaN(t) ? null : t;
}

function questionHasOption(question, optionId) {
  const options = question.options || [];
  return options.some((option) => option.optionId === optionId);
}

/**
 * Mirrors typeCheckAnswerValue in pipeline.go: verifies value has the
 * shape expected by question.type and, for selection types, that every
 * referenced optionId exists. Returns null when the value is acceptable,
 * or a FieldErrorCode string otherwise.
 */
function typeCheckAnswerValue(question, value) {
  const spec = specForType(question.type);
  if (!spec) return FieldErrorCode.TYPE_MISMATCH;

  switch (spec.valueShape) {
    case "text":
      return typeof value === "string" ? null : FieldErrorCode.TYPE_MISMATCH;
    case "number":
      return isNumericValue(value) ? null : FieldErrorCode.TYPE_MISMATCH;
    case "date":
      return typeof value === "string" && parseFlexibleDate(value) !== null
        ? null
        : FieldErrorCode.TYPE_MISMATCH;
    case "boolean":
      return typeof value === "boolean" ? null : FieldErrorCode.TYPE_MISMATCH;
    case "optionSingle": {
      if (typeof value !== "string") return FieldErrorCode.TYPE_MISMATCH;
      return questionHasOption(question, value) ? null : FieldErrorCode.UNKNOWN_OPTION_ID;
    }
    case "optionMulti": {
      if (!Array.isArray(value)) return FieldErrorCode.TYPE_MISMATCH;
      for (const item of value) {
        if (typeof item !== "string") return FieldErrorCode.TYPE_MISMATCH;
        if (!questionHasOption(question, item)) return FieldErrorCode.UNKNOWN_OPTION_ID;
      }
      return null;
    }
    case "location":
      return isValidLocationShape(value) ? null : FieldErrorCode.TYPE_MISMATCH;
    case "structured":
      return value === null || value === undefined ? FieldErrorCode.TYPE_MISMATCH : null;
    default:
      return FieldErrorCode.TYPE_MISMATCH;
  }
}

/** Mirrors isValidLocationShape in pipeline.go (minimal GeoJSON Point shape). */
function isValidLocationShape(value) {
  if (!isPlainObject(value)) return false;
  const coords = value.coordinates;
  if (!Array.isArray(coords) || coords.length !== 2) return false;
  return coords.every(isNumericValue);
}

function valuesEqual(a, b) {
  if (isNumericValue(a) && isNumericValue(b)) return a === b;
  if (typeof a === "string" && typeof b === "string") return a === b;
  if (typeof a === "boolean" && typeof b === "boolean") return a === b;
  return a === b;
}

function anyValueEquals(value, candidates) {
  return (candidates || []).some((candidate) => valuesEqual(value, candidate));
}

function containsAnyOf(value, candidates) {
  if (!Array.isArray(value)) return false;
  return value.some((item) => anyValueEquals(item, candidates));
}

function containsAllOf(value, candidates) {
  if (!Array.isArray(value)) return false;
  return (candidates || []).every((candidate) => value.some((item) => valuesEqual(item, candidate)));
}

/**
 * Mirrors compareOrdered in pipeline.go: compares value against the first
 * element of candidates for number/date shapes. Returns null when the
 * comparison cannot be evaluated (treated by callers as a non-match).
 */
function compareOrdered(value, candidates, shape) {
  if (!candidates || candidates.length === 0) return null;
  const target = candidates[0];
  if (shape === "number") {
    if (!isNumericValue(value) || !isNumericValue(target)) return null;
    return value < target ? -1 : value > target ? 1 : 0;
  }
  if (shape === "date") {
    const a = parseFlexibleDate(value);
    const b = parseFlexibleDate(target);
    if (a === null || b === null) return null;
    return a < b ? -1 : a > b ? 1 : 0;
  }
  return null;
}

/**
 * Mirrors evaluateCondition in pipeline.go: evaluates cond against the
 * effective value of the referenced question. Never throws on unexpected
 * shapes; an operator that cannot be evaluated conservatively evaluates to
 * false (hide-by-default), matching the backend.
 */
function evaluateCondition(cond, value, hasValue, refShape) {
  switch (cond.operator) {
    case "isEmpty":
      return !hasValue || isEmptyAnswerValue(value);
    case "isNotEmpty":
      return hasValue && !isEmptyAnswerValue(value);
    case "isChecked":
      return hasValue && value === true;
    case "isNotChecked":
      return !hasValue || value !== true;
    case "equals":
      return hasValue && anyValueEquals(value, cond.values);
    case "notEquals":
      return !hasValue || !anyValueEquals(value, cond.values);
    case "in":
      return hasValue && anyValueEquals(value, cond.values);
    case "notIn":
      return !hasValue || !anyValueEquals(value, cond.values);
    case "containsAny":
      return hasValue && containsAnyOf(value, cond.values);
    case "containsAll":
      return hasValue && containsAllOf(value, cond.values);
    case "greaterThan": {
      const cmp = compareOrdered(value, cond.values, refShape);
      return hasValue && cmp !== null && cmp > 0;
    }
    case "greaterOrEqual": {
      const cmp = compareOrdered(value, cond.values, refShape);
      return hasValue && cmp !== null && cmp >= 0;
    }
    case "lessThan": {
      const cmp = compareOrdered(value, cond.values, refShape);
      return hasValue && cmp !== null && cmp < 0;
    }
    case "lessOrEqual": {
      const cmp = compareOrdered(value, cond.values, refShape);
      return hasValue && cmp !== null && cmp <= 0;
    }
    default:
      return false;
  }
}

/**
 * Compiles a "pattern" validation rule's regular expression. See the file
 * header for the RE2/JS RegExp discrepancy this cannot fully close: only
 * the source-length bound is mirrored here, not RE2's linear-time
 * matching guarantee.
 */
function compileSafePattern(pattern) {
  if (typeof pattern !== "string" || pattern.length > MAX_PATTERN_LENGTH) return null;
  try {
    return new RegExp(pattern);
  } catch (e) {
    return null;
  }
}

function applyValidationRules(question, value) {
  const errors = [];
  for (const rule of question.validation || []) {
    const params = rule.params || {};
    switch (rule.type) {
      case "minLength": {
        if (typeof value === "string" && typeof params.min === "number") {
          if (value.length < params.min) errors.push(FieldErrorCode.MIN_LENGTH);
        }
        break;
      }
      case "maxLength": {
        if (typeof value === "string" && typeof params.max === "number") {
          if (value.length > params.max) errors.push(FieldErrorCode.MAX_LENGTH);
        }
        break;
      }
      case "pattern": {
        if (typeof value === "string" && typeof params.pattern === "string") {
          const re = compileSafePattern(params.pattern);
          if (!re || !re.test(value)) errors.push(FieldErrorCode.PATTERN);
        }
        break;
      }
      case "numberRange": {
        if (isNumericValue(value)) {
          if (typeof params.min === "number" && value < params.min) errors.push(FieldErrorCode.NUMBER_RANGE);
          else if (typeof params.max === "number" && value > params.max) errors.push(FieldErrorCode.NUMBER_RANGE);
        }
        break;
      }
      case "dateRange": {
        const t = parseFlexibleDate(value);
        if (t !== null) {
          const min = typeof params.min === "string" ? parseFlexibleDate(params.min) : null;
          const max = typeof params.max === "string" ? parseFlexibleDate(params.max) : null;
          if (min !== null && t < min) errors.push(FieldErrorCode.DATE_RANGE);
          else if (max !== null && t > max) errors.push(FieldErrorCode.DATE_RANGE);
        }
        break;
      }
      case "itemCount": {
        if (Array.isArray(value)) {
          const count = value.length;
          if (typeof params.min === "number" && count < params.min) errors.push(FieldErrorCode.ITEM_COUNT);
          else if (typeof params.max === "number" && count > params.max) errors.push(FieldErrorCode.ITEM_COUNT);
        }
        break;
      }
      default:
        // tableCellRules / attachmentLimit are delegated to dedicated
        // services, mirroring pipeline.go's applyValidationRules.
        break;
    }
  }
  return errors.map((code) => ({ questionId: question.questionId, code }));
}

/** Flattens definition.sections in declaration order (never reviewSections). */
function flattenTerrainQuestions(definition) {
  const questions = [];
  for (const section of definition.sections || []) {
    for (const question of section.questions || []) {
      questions.push(question);
    }
  }
  return questions;
}

function answerMapFrom(answers) {
  const map = new Map();
  for (const answer of answers || []) map.set(answer.questionId, answer.value);
  return map;
}

function computeVisibility(definition, rawById) {
  const questionsById = new Map(flattenTerrainQuestions(definition).map((question) => [question.questionId, question]));
  const effectiveById = new Map();
  const visibleSectionIds = [];
  const visibleQuestionIds = [];

  for (const section of definition.sections || []) {
    let sectionVisible = true;
    if (section.visibility) {
      const cond = section.visibility;
      const refQuestion = questionsById.get(cond.questionId);
      const refShape = refQuestion ? (specForType(refQuestion.type) || {}).valueShape : undefined;
      sectionVisible = evaluateCondition(cond, effectiveById.get(cond.questionId), effectiveById.has(cond.questionId), refShape);
    }
    if (sectionVisible) visibleSectionIds.push(section.sectionId);

    for (const question of section.questions || []) {
      let questionVisible = sectionVisible;
      if (questionVisible && question.visibility) {
        const cond = question.visibility;
        const refQuestion = questionsById.get(cond.questionId);
        const refShape = refQuestion ? (specForType(refQuestion.type) || {}).valueShape : undefined;
        questionVisible = evaluateCondition(cond, effectiveById.get(cond.questionId), effectiveById.has(cond.questionId), refShape);
      }
      if (questionVisible) {
        visibleQuestionIds.push(question.questionId);
        if (rawById.has(question.questionId)) effectiveById.set(question.questionId, rawById.get(question.questionId));
      }
    }
  }
  return { visibleSectionIds, visibleQuestionIds };
}

/** Evaluación UX pura: no aplica obligatoriedad ni elimina entradas incompletas visibles. */
export function evaluateVisibility(definition, answers) {
  return computeVisibility(definition || {}, answerMapFrom(answers));
}

function reviewQuestionIdSet(definition) {
  const ids = new Set();
  for (const section of definition.reviewSections || []) {
    for (const question of section.questions || []) {
      ids.add(question.questionId);
    }
  }
  return ids;
}

function sortFieldErrors(errors) {
  return errors.slice().sort((a, b) => {
    if (a.questionId !== b.questionId) return a.questionId < b.questionId ? -1 : 1;
    if (a.code !== b.code) return a.code < b.code ? -1 : 1;
    return 0;
  });
}

/**
 * runValidationPipeline mirrors RunValidationPipeline in pipeline.go: it
 * runs the deterministic pipeline (canonicalize by stable ID -> reject
 * duplicate/unknown/system-controlled IDs -> type-check -> compute
 * visibility in definition order -> strip hidden values -> apply
 * required/typed validations only to visible questions) over a
 * TemplateDefinitionDTO-shaped `definition` and an array of
 * AnswerDTO-shaped `answers`.
 *
 * The result is deterministic with respect to the order of `answers`:
 * permuting it never changes the output, because every step keys off
 * questionId and iterates the definition's own order.
 *
 * For UX feedback only; the backend remains authoritative (see file
 * header).
 *
 * @param {object} definition TemplateDefinitionDTO-shaped object.
 * @param {Array<{questionId: string, value: unknown}>} answers
 * @returns {{
 *   accepted: boolean,
 *   normalizedAnswers: Array<{questionId: string, value: unknown}>,
 *   visibleQuestionIds: string[],
 *   fieldErrors: Array<{questionId: string, code: string}>,
 * }}
 */
export function runValidationPipeline(definition, answers) {
  const terrainQuestions = flattenTerrainQuestions(definition);
  const reviewQuestionIds = reviewQuestionIdSet(definition);

  const questionsById = new Map();
  const orderedQuestionIds = [];
  for (const question of terrainQuestions) {
    questionsById.set(question.questionId, question);
    orderedQuestionIds.push(question.questionId);
  }

  const errors = [];
  // suppressedIds holds every questionId that already produced a
  // disqualifying FieldError (duplicate submission, or a type/option
  // check failure) whose value can no longer be trusted. A suppressed
  // question is completely skipped in the required/normalization step
  // below: it never additionally reports REQUIRED and never appears in
  // normalizedAnswers, so each disqualifying cause is reported exactly
  // once instead of cascading into a second, redundant error.
  const suppressedIds = new Set();

  // Step 1+2: canonicalize by stable ID; reject duplicate, unknown and
  // system-controlled IDs. Unknown and system-controlled answers never
  // correspond to a terrain question (they are absent from
  // questionsById/orderedQuestionIds), so they are automatically excluded
  // from every later step without needing suppressedIds. A duplicate,
  // however, *is* a known terrain question: its previously stored value
  // is removed and the id is suppressed so it is excluded from
  // normalizedAnswers and never additionally reports REQUIRED.
  const rawById = new Map();
  const seen = new Set();
  for (const answer of answers || []) {
    const qid = answer.questionId;
    if (seen.has(qid)) {
      errors.push({ questionId: qid, code: FieldErrorCode.DUPLICATE_QUESTION_ID });
      suppressedIds.add(qid);
      rawById.delete(qid);
      continue;
    }
    seen.add(qid);

    if (reviewQuestionIds.has(qid)) {
      errors.push({ questionId: qid, code: FieldErrorCode.SYSTEM_CONTROLLED_FIELD });
      continue;
    }

    if (!questionsById.has(qid)) {
      errors.push({ questionId: qid, code: FieldErrorCode.UNKNOWN_QUESTION_ID });
      continue;
    }

    rawById.set(qid, answer.value);
  }

  // Step 3: type-check answers and option IDs for every known,
  // non-duplicate, non-system-controlled answer. A value that fails
  // type-check or references an unknown option is removed from rawById
  // and its id suppressed, so the disqualifying error is reported exactly
  // once and never cascades into an additional REQUIRED error.
  for (const qid of orderedQuestionIds) {
    if (!rawById.has(qid)) continue;
    const question = questionsById.get(qid);
    const errorCode = typeCheckAnswerValue(question, rawById.get(qid));
    if (errorCode) {
      errors.push({ questionId: qid, code: errorCode });
      suppressedIds.add(qid);
      rawById.delete(qid);
    }
  }

  // Step 4: compute visible questions in definition order. A Condición
  // only ever references a strictly earlier question, so a single
  // left-to-right pass suffices.
  const visibility = computeVisibility(definition, rawById);
  const visibleOrder = visibility.visibleQuestionIds;

  // Step 5+6: strip hidden values from the normalized answer set; apply
  // required/typed validations only to visible questions. A suppressed
  // question (duplicate or failed type/option check) is skipped entirely:
  // its disqualifying error was already reported once in step 1-3 and it
  // never additionally reports REQUIRED nor appears in normalizedAnswers.
  const normalized = [];
  for (const qid of visibleOrder) {
    if (suppressedIds.has(qid)) continue;

    const question = questionsById.get(qid);
    const hasValue = rawById.has(qid);
    const value = hasValue ? rawById.get(qid) : undefined;

    if (!hasValue) {
      if (question.required) {
        errors.push({ questionId: qid, code: FieldErrorCode.REQUIRED });
      }
      continue;
    }

    // A present-but-empty value (empty string, empty array) is still
    // normalized and still runs its typed validations: emptiness is only
    // "no answer" for the purpose of the required check, not for whether
    // the value is included/validated. This lets an empty multiSelect
    // answer be reported as ITEM_COUNT rather than silently dropped.
    if (question.required && isEmptyAnswerValue(value)) {
      errors.push({ questionId: qid, code: FieldErrorCode.REQUIRED });
    }

    normalized.push({ questionId: qid, value });
    errors.push(...applyValidationRules(question, value));
  }

  const sortedErrors = sortFieldErrors(errors);

  return {
    accepted: sortedErrors.length === 0,
    normalizedAnswers: normalized,
    visibleSectionIds: visibility.visibleSectionIds,
    visibleQuestionIds: visibleOrder,
    fieldErrors: sortedErrors,
  };
}
